import type { CurrentUser } from '@/api/types';

export interface ViewDef {
  path: string;
  labelKey?: string;
  permission?: string;
  /** The user needs at least one of these role codes in addition to permission. */
  anyRoles?: readonly string[];
}

export const VIEWS = {
  home: { path: '/', labelKey: 'nav.home' },
  dashboard: {
    path: '/gerencia',
    labelKey: 'nav.dashboard',
    permission: 'dashboard.view_gerencia',
  },
  sites: {
    path: '/organizacion/sedes',
    labelKey: 'nav.sites',
    permission: 'organization.view_site',
  },
  departments: {
    path: '/organizacion/areas',
    labelKey: 'nav.departments',
    permission: 'organization.view_department',
  },
  positions: {
    path: '/organizacion/cargos',
    labelKey: 'nav.positions',
    permission: 'organization.view_position',
  },
  quickPicks: {
    path: '/organizacion/sugerencias-rapidas',
    labelKey: 'nav.quickPicks',
    permission: 'organization.view_quick_pick',
  },
  themes: {
    path: '/organizacion/temas',
    labelKey: 'nav.themes',
    permission: 'organization.view_manual',
  },
  versionReview: {
    path: '/organizacion/revisar-versiones',
    labelKey: 'nav.versionReview',
    permission: 'organization.view_manual',
  },
  campaigns: {
    path: '/evaluacion/campanas',
    labelKey: 'nav.campaigns',
    permission: 'evaluations.view_campaign',
  },
  newCampaign: { path: '/evaluacion/campanas/nueva', permission: 'evaluations.manage_campaign' },
  campaignTracking: {
    path: '/evaluacion/seguimiento',
    labelKey: 'nav.campaingDashboard',
    permission: 'evaluations.view_campaign',
  },
  myCampaigns: {
    path: '/evaluacion/mis-campanas',
    labelKey: 'nav.myCampaigns',
    permission: 'evaluations.view_campaign',
  },
  employees: {
    path: '/trabajadores',
    labelKey: 'nav.employees',
    permission: 'employees.view_employee',
  },
  myProfile: { path: '/mi-ficha', permission: 'employees.view_own_profile' },
  roles: { path: '/roles', labelKey: 'nav.roles', permission: 'iam.view_role' },
  permissions: { path: '/permissions', labelKey: 'nav.permissions', permission: 'iam.view_role' },
  hrHome: { path: '/rrhh', anyRoles: ['HR'] },
  managerHome: { path: '/jefe-area', anyRoles: ['MANAGER'] },
  employeeHome: { path: '/inicio', anyRoles: ['EMPLOYEE'] },
  manualReader: {
    path: '/inicio/capacitaciones/:id/manuales/:versionId',
    permission: 'evaluations.read_campaign_manual',
  },
  manualDetail: { path: '/organizacion/manuales/:id', permission: 'organization.view_department' },
  staffingRequests: {
    path: '/convocatorias/solicitudes',
    labelKey: 'nav.staffingRequests',
    permission: 'staffing.view_staffing_request',
  },
} satisfies Record<string, ViewDef>;

export type ViewKey = keyof typeof VIEWS;
export type NavigableViewKey = {
  [K in ViewKey]: (typeof VIEWS)[K] extends { labelKey: string } ? K : never;
}[ViewKey];

export const roleHomes: Partial<Record<string, ViewKey>> = {
  HR: 'hrHome',
  MANAGER: 'managerHome',
  EMPLOYEE: 'employeeHome',
  EXECUTIVE: 'dashboard',
  SUPPORT_TI: 'dashboard',
};

/** UI convenience only: APIs remain the authority for every operation. */
export function canSeeView(user: CurrentUser | null, view: ViewDef): boolean {
  if (!user) return false;
  if (user.scope.is_global) return true;
  if (view.permission && !user.permissions.includes(view.permission)) return false;
  if (view.anyRoles && !view.anyRoles.some((role) => user.role_codes.includes(role))) return false;
  return true;
}
